import { useMemo, useState, useEffect } from "react";
import { Bookmark, BookmarkCheck, CheckCircle2, ExternalLink, Trash2, DollarSign, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useSavedOpportunities,
  type SavedStatus,
} from "@/hooks/useSavedOpportunities";
import { Link } from "react-router-dom";

interface SavedOpportunitiesTabProps {
  onCountChange?: (count: number) => void;
}

type Filter = "all" | SavedStatus;

const SavedOpportunitiesTab = ({ onCountChange }: SavedOpportunitiesTabProps) => {
  const { items, loading, save, markApplied, removeById } = useSavedOpportunities();
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    if (!loading) onCountChange?.(items.length);
  }, [items.length, loading, onCountChange]);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((i) => i.status === filter)),
    [items, filter],
  );

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border p-10 text-center">
        <Bookmark className="h-6 w-6 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Save grants surfaced by your AI Enhanced Strategic Assessment to track them here.
        </p>
        <Link to="/coach">
          <Button size="sm" variant="outline">Open AI Enhanced Strategic Assessment</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(["all", "saved", "applied"] as Filter[]).map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            className={filter === f ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f === "saved" ? "Saved" : "Applied"}
            <span className="ml-1.5 text-xs opacity-80">
              {f === "all"
                ? items.length
                : items.filter((i) => i.status === f).length}
            </span>
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((it) => (
          <div
            key={it.id}
            className="rounded-xl border border-border bg-card p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-display text-sm font-semibold text-foreground">
                    {it.title}
                  </h3>
                  {it.source_type ? (
                    <Badge variant="outline" className="text-[10px]">
                      {it.source_type}
                    </Badge>
                  ) : null}
                  <Badge
                    variant="secondary"
                    className={`text-[10px] ${
                      it.status === "applied"
                        ? "bg-primary/15 text-primary border-primary/30"
                        : ""
                    }`}
                  >
                    {it.status === "applied" ? (
                      <>
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Applied
                      </>
                    ) : (
                      <>
                        <BookmarkCheck className="mr-1 h-3 w-3" />
                        Saved
                      </>
                    )}
                  </Badge>
                </div>
                {it.source ? (
                  <p className="mt-1 text-xs text-muted-foreground">{it.source}</p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {it.amount ? (
                    <span className="inline-flex items-center gap-1 text-accent">
                      <DollarSign className="h-3 w-3" />
                      {it.amount}
                    </span>
                  ) : null}
                  {it.deadline ? (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {it.deadline}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {it.url ? (
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Open <ExternalLink className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              {it.status === "applied" ? (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => save(it)}
                >
                  <BookmarkCheck className="mr-1 h-3.5 w-3.5" />
                  Move to Saved
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-7 px-2.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                  onClick={() => markApplied(it)}
                >
                  <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                  Mark Applied
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2.5 text-xs text-muted-foreground hover:text-destructive"
                onClick={() => removeById(it.id)}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SavedOpportunitiesTab;
